import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Typography } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import mapboxgl from "mapbox-gl";
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';

import "mapbox-gl/dist/mapbox-gl.css";
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

// Esquema de validación
const schema = yup.object().shape({
  locnom: yup.string().required("El nombre es obligatorio"),
  locdes: yup.string().max(255, "Máximo 255 caracteres"),
});

const LocalizationForm = ({ open, handleClose, onSubmit, defaultValues = {} }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      locnom: defaultValues.locnom || "",
      locdes: defaultValues.locdes || "",
      loclat: defaultValues.loclat || -16.3989, // Default Arequipa
      loclng: defaultValues.loclng || -71.5350,
      loctip: defaultValues.loctip || "RUTA",
    },
  });

  const loclat = watch("loclat");
  const loclng = watch("loclng");

  //const [map, setMap] = useState(null);
  //const [marker, setMarker] = useState(null);

  const mapRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const timeout = setTimeout(() => {
      const container = document.getElementById("mapa-localizacion");
      if (!container || mapRef.current) return;

      mapRef.current = new mapboxgl.Map({
        container,
        style: "mapbox://styles/mapbox/streets-v11",
        center: [loclng, loclat],
        zoom: 12,
      });

      // ✅ Crear el marcador
      markerRef.current = new mapboxgl.Marker({ draggable: true })
        .setLngLat([loclng, loclat])
        .addTo(mapRef.current);

      // ✅ Evento al arrastrar
      markerRef.current.on("dragend", () => {
        const lngLat = markerRef.current.getLngLat();
        setValue("loclat", lngLat.lat);
        setValue("loclng", lngLat.lng);
      });

      // ✅ Añadir geocoder
      const geocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl,
        placeholder: 'Buscar lugar, ciudad o coordenadas...',
        marker: false,
      });

      mapRef.current.addControl(geocoder);

      geocoder.on("result", (e) => {
        const { center } = e.result;
        markerRef.current.setLngLat(center);
        mapRef.current.flyTo({ center, zoom: 15 });
        setValue("loclat", center[1]);
        setValue("loclng", center[0]);
      });
    }, 100);

    return () => {
      clearTimeout(timeout);
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [open]);

  const handleFormClose = () => {
    reset();
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleFormClose} fullWidth maxWidth="md" keepMounted>
      <DialogTitle>{defaultValues?.locid ? "Editar Localización" : "Nueva Localización"}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <TextField
            label="Nombre"
            fullWidth
            margin="normal"
            {...register("locnom")}
            error={!!errors.locnom}
            helperText={errors.locnom?.message}
          />

          <TextField
            label="Descripción"
            fullWidth
            margin="normal"
            multiline
            rows={2}
            {...register("locdes")}
            error={!!errors.locdes}
            helperText={errors.locdes?.message}
          />

          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
            Selecciona la ubicación en el mapa (arrastra el marcador)
          </Typography>

          <div id="mapa-localizacion" style={{ width: "100%", height: "300px", borderRadius: 8, marginTop: 8 }} />

          <TextField
            label="Latitud"
            fullWidth
            margin="normal"
            {...register("loclat")}
            type="number"
            inputProps={{ step: "0.000001" }}
          />

          <TextField
            label="Longitud"
            fullWidth
            margin="normal"
            {...register("loclng")}
            type="number"
            inputProps={{ step: "0.000001" }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleFormClose}>Cancelar</Button>
          <Button type="submit" variant="contained">
            {defaultValues?.locid ? "Actualizar" : "Guardar"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default LocalizationForm;
